import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  Settings, 
  ToggleLeft, 
  ToggleRight, 
  Save, 
  RefreshCw,
  Share,
  Calendar,
  BarChart3,
  Link,
  AlertCircle,
  CheckCircle,
  Send,
  Video,
  Wand2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'react-hot-toast';
import { featureFlagService, FeatureFlag } from '../../services/featureFlagService';
import { invalidateFeatureFlags } from '../../hooks/useFeatureFlags';

const FeatureFlagsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>({});

  const { data: flags, isLoading, error } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => featureFlagService.getAllFlags(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ flagName, isEnabled }: { flagName: string; isEnabled: boolean }) =>
      featureFlagService.updateFlag(flagName, isEnabled),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      // Invalidate the public flags cache for real-time updates
      await invalidateFeatureFlags();
      toast.success('Feature flag updated successfully!');
    },
    onError: (error) => {
      console.error('Failed to update feature flag:', error);
      toast.error('Failed to update feature flag');
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: (updates: Record<string, boolean>) =>
      featureFlagService.bulkUpdateFlags(updates),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      // Invalidate the public flags cache for real-time updates
      await invalidateFeatureFlags();
      setPendingChanges({});
      toast.success('All feature flags updated successfully!');
    },
    onError: (error) => {
      console.error('Failed to update feature flags:', error);
      toast.error('Failed to update feature flags');
    },
  });

  const notifyProFeaturesMutation = useMutation({
    mutationFn: () => featureFlagService.notifyWaitlist('pro_features'),
    onSuccess: (data) => {
      toast.success(data.message || 'Pro features notification sent to all waitlist subscribers!');
    },
    onError: (error) => {
      console.error('Failed to notify Pro features waitlist:', error);
      toast.error('Failed to notify Pro features waitlist');
    },
  });

  const handleToggle = (flagName: string, currentValue: boolean) => {
    const newValue = !currentValue;
    setPendingChanges(prev => ({
      ...prev,
      [flagName]: newValue
    }));
  };

  const handleSaveAll = () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.error('No changes to save');
      return;
    }
    bulkUpdateMutation.mutate(pendingChanges);
  };

  const handleResetChanges = () => {
    setPendingChanges({});
    toast.success('Changes reset');
  };

  const getEffectiveValue = (flag: FeatureFlag) => {
    return pendingChanges.hasOwnProperty(flag.flag_name) 
      ? pendingChanges[flag.flag_name] 
      : flag.is_enabled;
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const getFlagIcon = (flagName: string) => {
    switch (flagName) {
      case 'publishing': return Share;
      case 'scheduling': return Calendar;
      case 'social_analytics': return BarChart3;
      case 'social_connections': return Link;
      case 'video_generation': return Video;
      case 'one_click_creator': return Wand2;
      default: return Settings;
    }
  };

  const getFlagColor = (flagName: string, isEnabled: boolean) => {
    if (!isEnabled) return 'text-gray-400';
    
    switch (flagName) {
      case 'publishing': return 'text-blue-600';
      case 'scheduling': return 'text-purple-600';
      case 'social_analytics': return 'text-green-600';
      case 'social_connections': return 'text-orange-600';
      case 'video_generation': return 'text-red-600';
      case 'one_click_creator': return 'text-indigo-600';
      default: return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="text-center py-16">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Failed to load feature flags
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please try refreshing the page
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Feature Flags
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Control which features are enabled for all users. Changes take effect immediately.
          </p>
          
          {/* Pro Features Notification */}
          <div className="mt-4">
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-emerald-800 dark:text-emerald-200">
                      Notify Pro Features Waitlist
                    </h3>
                    <p className="text-sm text-emerald-600 dark:text-emerald-300 mt-1">
                      Send consolidated email about all Pro features: Social Platform Connection, Analytics, Multi-platform Publishing, and Post Scheduling
                    </p>
                  </div>
                  <Button
                    onClick={() => notifyProFeaturesMutation.mutate()}
                    disabled={notifyProFeaturesMutation.isPending}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {notifyProFeaturesMutation.isPending ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Notify All Waitlist
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Pending Changes Banner */}
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800 dark:text-amber-200 font-medium">
                  You have {Object.keys(pendingChanges).length} unsaved change(s)
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetChanges}
                  className="text-amber-700 border-amber-300 hover:bg-amber-100"
                >
                  Reset
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveAll}
                  disabled={bulkUpdateMutation.isPending}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {bulkUpdateMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save All Changes
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Feature Flags Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {flags?.map((flag) => {
            const Icon = getFlagIcon(flag.flag_name);
            const effectiveValue = getEffectiveValue(flag);
            const hasChange = pendingChanges.hasOwnProperty(flag.flag_name);
            
            return (
              <motion.div
                key={flag.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className={`transition-all duration-200 ${hasChange ? 'ring-2 ring-amber-400 bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-6 h-6 ${getFlagColor(flag.flag_name, effectiveValue)}`} />
                        <div>
                          <CardTitle className="text-lg capitalize">
                            {flag.flag_name.replace('_', ' ')}
                          </CardTitle>
                          <CardDescription className="text-sm">
                            {flag.description}
                          </CardDescription>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {hasChange && (
                          <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                        )}
                        <Switch
                          checked={effectiveValue}
                          onCheckedChange={() => handleToggle(flag.flag_name, flag.is_enabled)}
                          disabled={updateMutation.isPending}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {effectiveValue ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-green-700 dark:text-green-400 font-medium">
                              Enabled
                            </span>
                          </>
                        ) : (
                          <>
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                            <span className="text-gray-500 dark:text-gray-400">
                              Disabled
                            </span>
                          </>
                        )}
                      </div>
                      
                      {/* Individual notify buttons removed - use consolidated notification above */}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Info Card */}
        <Card className="mt-8 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="text-blue-900 dark:text-blue-100 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Important Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="text-blue-800 dark:text-blue-200 space-y-2">
            <p>• Changes to feature flags take effect immediately for all users</p>
            <p>• Disabling features will show "Coming Soon" messages to users</p>
            <p>• Users can join waitlists for disabled features from anywhere in the app</p>
            <p>• Use the "Notify All Waitlist" button above to send Pro features announcement to all waitlist subscribers</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FeatureFlagsPage;